import { useState, useMemo, useCallback } from 'react';

export interface SearchableItem {
  id: string;
  title: string;
  [key: string]: any;
}

export interface UseTemplateSearchProps<T extends SearchableItem> {
  items: T[];
  searchFields?: (keyof T)[];
}

export interface UseTemplateSearchReturn<T> {
  searchValue: string;
  filteredItems: T[];
  handleSearch: (value: string) => void;
  clearSearch: () => void;
  hasResults: boolean;
  totalCount: number;
  filteredCount: number;
}

/**
 * Custom hook for searching through template items
 * Provides flexible search functionality with multiple field support
 */
export const useTemplateSearch = <T extends SearchableItem>({
  items,
  searchFields = ['title'],
}: UseTemplateSearchProps<T>): UseTemplateSearchReturn<T> => {
  const [searchValue, setSearchValue] = useState<string>('');

  const filteredItems = useMemo(() => {
    if (!searchValue.trim()) {
      return items;
    }

    const searchTerm = searchValue.toLowerCase().trim();

    return items.filter((item) =>
      searchFields.some((field) => {
        const fieldValue = item[field];
        if (typeof fieldValue === 'string') {
          return fieldValue.toLowerCase().includes(searchTerm);
        }
        return false;
      })
    );
  }, [items, searchValue, searchFields]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const hasResults = filteredItems.length > 0;
  const totalCount = items.length;
  const filteredCount = filteredItems.length;

  return {
    searchValue,
    filteredItems,
    handleSearch,
    clearSearch,
    hasResults,
    totalCount,
    filteredCount,
  };
};
